import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRightLeft, PlayCircle, CalendarX, Check, Clock3, Copy, Database, Flame, MessageSquareText, RefreshCw, RotateCw, ShieldCheck, Stethoscope, TrendingUp, UserX, X } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRosterMoves, useStatIntel, type GoalHighlight, type LeagueSection, type MoveFlag, type RosterMove, type StatIntelLine, type StatIntelResponse } from '@/hooks/useStats';
import { useActiveInjuries } from '@/hooks/useInjuries';
import { saveTextVoiceExample, useTextVoiceExamples } from '@/hooks/useTextVoiceExamples';
import { apiGet, apiPost } from '@/lib/api';
import {
  adaptTrainingExample,
  buildDraftText,
  buildTouchpoints,
  displayName,
  flagStatusFor,
  scenarioForLine,
  type DraftVariant,
  type Touchpoint,
} from '@/lib/draftText';
import { birthdayTouchpoint, daysFromTodayLabel, formatDob, formatPlusMinus, formatSavePct, formatToi } from '@/lib/formatters';
import type { FlagStatus, FlagType } from '@/lib/tokens';

type SortKey = 'jersey' | 'player' | 'position' | 'team' | 'gameDate' | 'opponent' | 'goals' | 'assists' | 'points' | 'plusMinus' | 'pim' | 'shots' | 'hits' | 'blocked' | 'giveaways' | 'takeaways' | 'toi' | 'goalie';
type SortDirection = 'asc' | 'desc';
type StatSort = { key: SortKey; direction: SortDirection };
type SourceRefreshResult = { statLinesIngested?: number; errors?: unknown[] };
type NhlIngestResult = SourceRefreshResult & {
  limit?: number;
  offset?: number;
  nextOffset?: number | null;
  hasMore?: boolean;
  totalEligible?: number;
  playersScanned?: number;
  rateLimited?: boolean;
};

type NhlBatchSummary = {
  statLinesIngested: number;
  errors: unknown[];
  playersScanned: number;
  totalEligible: number;
  batches: number;
};

const SORT_KEYS: SortKey[] = [
  'jersey',
  'player',
  'position',
  'team',
  'gameDate',
  'opponent',
  'goals',
  'assists',
  'points',
  'plusMinus',
  'pim',
  'shots',
  'hits',
  'blocked',
  'giveaways',
  'takeaways',
  'toi',
  'goalie',
];

const WATCH_FLAGS: FlagType[] = ['giveaway-watch', 'low-minutes', 'no-shot-toi', 'injury-bench'];
const TOI_FLAG_TYPES: FlagType[] = ['low-minutes', 'no-shot-toi'];

const FLAG_COPY: Record<FlagType, { label: string; ok: string; warn: string; critical: string }> = {
  'giveaway-watch': {
    label: 'Giveaways',
    ok: 'OK',
    warn: '3+ giveaways',
    critical: 'High giveaway risk',
  },
  'low-minutes': {
    label: 'Low shift',
    ok: 'Normal',
    warn: 'Usage drop',
    critical: 'Critical drop',
  },
  'no-shot-toi': {
    label: 'No shots',
    ok: 'OK',
    warn: '10+ TOI / 0 SOG',
    critical: 'Shot concern',
  },
  'injury-bench': {
    label: 'IR watch',
    ok: 'Clear',
    warn: 'Check in',
    critical: 'IR conflict',
  },
};

const FLAG_TONE: Record<FlagStatus, string> = {
  ok: 'border-border/80 bg-white text-muted',
  warn: 'border-flag-warn/30 bg-flag-warn/5 text-flag-warn',
  critical: 'border-flag-critical/30 bg-flag-critical/5 text-flag-critical',
};

async function refreshAllNhlBatches(setStatus: (message: string) => void): Promise<NhlBatchSummary> {
  const batchSize = 50;
  const maxBatches = 50;
  let offset = 0;
  let batches = 0;
  let statLinesIngested = 0;
  let playersScanned = 0;
  let totalEligible = 0;
  const errors: unknown[] = [];
  const seenOffsets = new Set<number>();

  while (batches < maxBatches) {
    if (seenOffsets.has(offset)) {
      throw new Error(`NHL refresh stopped because offset ${offset} repeated.`);
    }
    seenOffsets.add(offset);
    setStatus(`Running NHL source check: batch ${batches + 1}${totalEligible ? `, ${playersScanned}/${totalEligible} clients scanned` : ''}...`);

    const result = await apiPost<NhlIngestResult>(`/api/admin/ingest-nhl?days=2&limit=${batchSize}&offset=${offset}`, {});
    batches += 1;
    statLinesIngested += result.statLinesIngested ?? 0;
    playersScanned += result.playersScanned ?? 0;
    totalEligible = result.totalEligible ?? totalEligible;
    if (Array.isArray(result.errors)) errors.push(...result.errors);

    if (result.rateLimited) {
      throw new Error(`NHL source rate limited after ${playersScanned}/${totalEligible || 'unknown'} clients.`);
    }
    if (!result.hasMore || result.nextOffset == null) {
      return { statLinesIngested, errors, playersScanned, totalEligible, batches };
    }
    offset = result.nextOffset;
  }

  throw new Error(`NHL refresh stopped after ${maxBatches} batches before all clients were scanned.`);
}

export function StatIntel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDate = searchParams.get('date');
  const { data, isLoading, isError, refetch, isFetching } = useStatIntel(requestedDate);
  const activeInjuries = useActiveInjuries();
  const qc = useQueryClient();
  const [showInactiveFor, setShowInactiveFor] = useState<Record<string, boolean>>({});
  const [refreshStatus, setRefreshStatus] = useState<string | null>(null);
  const [refreshingSources, setRefreshingSources] = useState(false);
  const statSort = sortFromSearchParams(searchParams);

  const { sections, totalPlayed, trackedLeagues, sourceIssues, allLines } = useMemo(() => {
    const all = data?.leagueSections ?? [];
    const totalPlayed = all.reduce((acc, s) => acc + s.played.length, 0);
    const trackedLeagues = all.length;
    const sourceIssues = all.filter((s) => s.sourceStatus === 'error' || s.sourceStatus === 'stale' || s.sourceStatus === 'unverified').length;
    const allLines = all.flatMap((section) => [...section.played, ...section.didNotPlay]);
    return { sections: all, totalPlayed, trackedLeagues, sourceIssues, allLines };
  }, [data]);

  const touchpoints = useMemo(() => buildTouchpoints(allLines), [allLines]);

  const returningThisWeek = useMemo(() => {
    const items = activeInjuries.data?.items ?? [];
    return items
      .map((injury) => ({ injury, eta: daysFromTodayLabel(injury.projectedReturnDate) }))
      .filter(({ injury, eta }) => injury.status === 'active' && eta.days != null && eta.days >= 0 && eta.days <= 7)
      .sort((a, b) => (a.eta.days ?? 99) - (b.eta.days ?? 99))
      .slice(0, 4);
  }, [activeInjuries.data]);

  const lastRefresh = data?.lastRefreshAt
    ? new Date(data.lastRefreshAt).toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';
  const lastSourceCheck = data?.lastSourceCheckAt
    ? new Date(data.lastSourceCheckAt).toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

  async function refreshSources() {
    if (refreshingSources) return;
    setRefreshingSources(true);
    setRefreshStatus('Running NHL, AHL, ECHL, OHL, QMJHL, WHL, USHL, NAHL, NCAA, PWHL and European source checks...');
    try {
      const minorsRefresh = apiPost<SourceRefreshResult>('/api/admin/ingest-minor-leagues?days=7', {});
      const europeRefresh = apiPost<SourceRefreshResult>('/api/admin/ingest-europe?days=7', {});
      const [nhlResult, minorsResult, europeResult] = await Promise.allSettled([
        refreshAllNhlBatches(setRefreshStatus),
        minorsRefresh,
        europeRefresh,
      ]);
      await qc.invalidateQueries({ queryKey: ['stats', 'intel'] });
      await refetch();
      if (nhlResult.status === 'rejected' && minorsResult.status === 'rejected') {
        throw new Error(`${nhlResult.reason instanceof Error ? nhlResult.reason.message : 'NHL refresh failed'}; ${minorsResult.reason instanceof Error ? minorsResult.reason.message : 'minor-league refresh failed'}`);
      }
      const nhl = nhlResult.status === 'fulfilled' ? nhlResult.value : { statLinesIngested: 0, errors: [nhlResult.reason], playersScanned: 0, totalEligible: 0, batches: 0 };
      const minors = minorsResult.status === 'fulfilled' ? minorsResult.value : { statLinesIngested: 0, errors: [minorsResult.reason] };
      const europe = europeResult.status === 'fulfilled' ? europeResult.value : { statLinesIngested: 0, errors: [europeResult.reason] };
      const rowsUpdated = (nhl.statLinesIngested ?? 0) + (minors.statLinesIngested ?? 0) + (europe.statLinesIngested ?? 0);
      const errorCount = [nhl.errors, minors.errors, europe.errors].reduce((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0);
      const nhlScope = nhl.totalEligible
        ? ` NHL scanned ${nhl.playersScanned}/${nhl.totalEligible} clients across ${nhl.batches} batch${nhl.batches === 1 ? '' : 'es'}.`
        : '';
      setRefreshStatus(`Source check complete: ${rowsUpdated} rows updated.${nhlScope}${errorCount ? ` ${errorCount} issue${errorCount === 1 ? '' : 's'}.` : ''}`);
    } catch (error) {
      setRefreshStatus(error instanceof Error ? `Source check failed: ${error.message}` : 'Source check failed.');
      qc.invalidateQueries({ queryKey: ['stats', 'intel'] });
    } finally {
      setRefreshingSources(false);
    }
  }

  function updateSort(key: SortKey) {
    const direction: SortDirection = statSort.key === key && statSort.direction === 'asc' ? 'desc' : 'asc';
    const next = new URLSearchParams(searchParams);
    next.set('sort', key);
    next.set('dir', direction);
    setSearchParams(next, { replace: true });
  }

  return (
    <section className="space-y-6">
      <div className="command-panel overflow-hidden rounded-control border border-black/10 text-white">
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              Bartlett Hockey Command Center
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-normal">Stat Intel</h1>
            <p className="mt-1 max-w-2xl break-words text-sm text-white/70">
              Nightly player performance, flags, and game context from verified hockey feeds.
            </p>
          </div>
          <button
            onClick={refreshSources}
            disabled={refreshingSources}
            className="focus-ring inline-flex h-10 w-fit items-center gap-2 rounded-control border border-white/15 bg-white px-3 text-sm font-semibold text-bartlett shadow-sm transition-colors hover:bg-bartlettIce disabled:opacity-50"
          >
            <RefreshCw size={15} className={refreshingSources ? 'animate-spin' : ''} />
            <span>{refreshingSources ? 'Checking sources' : 'Refresh sources'}</span>
          </button>
        </div>
        <div className="grid border-t border-white/10 bg-white/[0.04] sm:grid-cols-4">
          <Metric icon={TrendingUp} label="Clients played" value={String(totalPlayed)} />
          <Metric icon={Clock3} label="Last ingest" value={lastRefresh} tone={data?.isStale ? 'warn' : 'default'} />
          <Metric icon={ShieldCheck} label="Tracked leagues" value={String(trackedLeagues)} />
          <Metric icon={Database} label="Source issues" value={String(sourceIssues)} tone={sourceIssues > 0 ? 'warn' : 'ok'} />
        </div>
      </div>

      <NahlMatchReview />
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-border bg-white px-4 py-2 text-[13px] text-muted">
        <span>Updates when you open or return to this page. Latest source check: <span className="font-mono text-ink">{lastSourceCheck}</span></span>
        <button
          type="button"
          onClick={() => {
            qc.invalidateQueries({ queryKey: ['stats', 'intel'] });
            refetch();
          }}
          disabled={isFetching}
          className="focus-ring inline-flex h-8 items-center gap-2 rounded-control border border-border px-2.5 font-semibold text-ink hover:bg-bartlettIce disabled:opacity-50"
        >
          <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} />
          <span>Reload table</span>
        </button>
      </div>

      {refreshStatus && (
        <div className="rounded-control border border-border bg-white px-4 py-2 text-sm text-muted">
          {refreshStatus}
        </div>
      )}

      {data?.isStale && (
        <div className="flex gap-3 rounded-control border border-flag-warn/30 bg-flag-warn/10 px-4 py-3 text-sm text-ink">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-flag-warn" />
          <div>
            <p className="font-semibold">Stat Intel is showing stale verified rows.</p>
            <p className="mt-1 text-muted">
              Last ingest was {lastRefresh}. Use the manual source refresh before treating this as the current morning read.
            </p>
          </div>
        </div>
      )}

      {returningThisWeek.length > 0 && (
        <div className="flex flex-col gap-3 rounded-control border border-bartlettGold/40 bg-bartlettGold/10 px-4 py-3 text-sm text-ink shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 gap-3">
            <Stethoscope className="mt-0.5 h-4 w-4 shrink-0 text-bartlett" />
            <div className="min-w-0">
              <p className="font-semibold">Return watch before calls</p>
              <p className="mt-1 text-muted">
                {returningThisWeek.length} active IR {returningThisWeek.length === 1 ? 'client is' : 'clients are'} projected back within seven days.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {returningThisWeek.map(({ injury, eta }) => {
              const name = injury.player?.displayName ?? [injury.player?.firstName, injury.player?.lastName].filter(Boolean).join(' ') ?? 'Unknown';
              return (
                <a
                  key={injury.id}
                  href={`/people/${injury.playerId}?tab=injuries`}
                  className="focus-ring inline-flex items-center gap-2 rounded-full border border-bartlettGold/40 bg-white px-2.5 py-1 font-mono text-[11px] text-bartlett hover:border-bartlett"
                  title={`Projected return ${formatDob(injury.projectedReturnDate)}`}
                >
                  <span>{name}</span>
                  <span className="text-muted">{eta.label}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {data?.noGames && !requestedDate && (
        <div className="flex flex-col gap-3 rounded-control border border-border bg-white px-4 py-3 text-sm text-ink shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 gap-3">
            <CalendarX className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
            <div className="min-w-0">
              <p className="font-semibold">No games last night</p>
              <p className="mt-1 text-muted">None of your clients played on {formatLongDate(data.gameDate)}.</p>
            </div>
          </div>
          {data.lastGameDate && (
            <Link
              to={`?date=${data.lastGameDate}`}
              className="focus-ring inline-flex h-10 w-fit items-center rounded-control border border-border px-3 font-semibold text-ink hover:bg-bartlettIce"
            >
              See {formatLongDate(data.lastGameDate)}
            </Link>
          )}
        </div>
      )}

      {requestedDate && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-bartlettGold/40 bg-bartlettGold/10 px-4 py-2 text-sm text-ink">
          <span>Showing {formatLongDate(requestedDate)}.</span>
          <button
            type="button"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('date');
              setSearchParams(next);
            }}
            className="focus-ring font-semibold text-bartlett underline"
          >
            Back to last night
          </button>
        </div>
      )}

      {data?.isTonight && !requestedDate && (
        <div className="rounded-control border border-bartlettGold/40 bg-bartlettGold/10 px-4 py-2 text-sm text-ink">
          Showing tonight&apos;s games so far ({formatLongDate(data.gameDate)}). Late games appear after the next refresh; the full night is ready by morning.
        </div>
      )}

      <RosterMovesPanel />

      {data && !data.noGames && <NeedsYouToday sections={sections} moves={data.moves ?? []} activeIrCount={activeInjuries.data?.items?.length ?? 0} />}

      <TouchpointsPanel touchpoints={touchpoints} />

      <div className="flex flex-col gap-2 border-b border-border/80 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-normal">Verified game rows</h2>
          <p className="mt-1 text-sm text-muted">
            Game date · <span className="font-mono">{data?.gameDate ?? '—'}</span>
            <span className="text-muted"> · Last ingest </span>
            <span className="font-mono">{lastRefresh}</span>
            {totalPlayed > 0 && (
              <span className="text-muted"> · {totalPlayed} clients played</span>
            )}
          </p>
        </div>
        <p className="text-sm text-muted">
          Sorted by <span className="font-mono text-ink">{sortLabel(statSort)}</span>. Click a table header to change it.
        </p>
      </div>

      {isLoading && <LoadingSections />}
      {isError && (
        <div className="rounded-control border border-flag-critical/30 bg-flag-critical/5 p-4 text-sm text-flag-critical">
          Couldn&apos;t load stat lines. Try refresh.
        </div>
      )}

      {data && sections.length === 0 && (
        <div className="py-12 text-center text-sm text-muted">No tracked league sections are configured.</div>
      )}

      {sections.length > 1 && <LeagueJumpBar sections={sections} />}

      {sections.map((section) => (
        <LeagueBlock
          key={section.key}
          section={section}
          statSort={statSort}
          onSort={updateSort}
          showInactive={showInactiveFor[section.key] ?? false}
          onToggleInactive={(v) => setShowInactiveFor((p) => ({ ...p, [section.key]: v }))}
        />
      ))}
    </section>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  tone = 'default',
}: {
  icon: typeof Database;
  label: string;
  value: string;
  tone?: 'default' | 'ok' | 'warn';
}) {
  return (
    <div className="border-t border-white/10 px-5 py-4 first:border-t-0 sm:border-l sm:border-t-0 sm:first:border-l-0">
      <div className="flex items-center gap-2 text-white/60">
        <Icon size={14} />
        <span className="font-mono text-[10px] uppercase tracking-[0.14em]">{label}</span>
      </div>
      <div
        className={[
          'mt-2 truncate text-[15px] font-semibold',
          tone === 'ok' ? 'text-bartlettGold' : tone === 'warn' ? 'text-flag-warn' : 'text-white',
        ].join(' ')}
      >
        {value}
      </div>
    </div>
  );
}

function TouchpointsPanel({ touchpoints }: { touchpoints: Touchpoint[] }) {
  const [draftLine, setDraftLine] = useState<StatIntelLine | null>(null);

  return (
    <section className="rounded-control border border-border bg-white p-3 shadow-sm sm:p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold tracking-normal">Reach-out touchpoints</h2>
          <p className="mt-1 text-sm text-muted">Birthdays, production, workload, and watch flags from today&apos;s Stat Intel.</p>
        </div>
        <span className="shrink-0 rounded-full border border-border bg-bartlettIce px-2 py-0.5 font-mono text-[11px] text-muted">
          {touchpoints.length}
        </span>
      </div>

      {touchpoints.length === 0 ? (
        <p className="mt-3 rounded-control border border-dashed border-border bg-bartlettIce/45 px-3 py-3 text-sm text-muted">
          No urgent birthday or stat touchpoints in the current feed.
        </p>
      ) : (
        <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
          {touchpoints.map((touchpoint) => {
            const name = displayName(touchpoint.line);
            return (
              <article key={touchpoint.id} className="flex min-w-0 items-start justify-between gap-2 rounded-control border border-border bg-bartlettIce/35 p-3">
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    <Link to={`/people/${touchpoint.line.playerId}`} className="focus-ring min-w-0 truncate rounded-sm text-sm font-semibold text-ink hover:text-bartlett">
                      {name}
                    </Link>
                    <span className="shrink-0 rounded-full border border-bartlettGold/40 bg-bartlettGold/15 px-1.5 py-0.5 font-mono text-[10px] text-bartlett">
                      {touchpoint.label}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-[12px] leading-5 text-muted">{touchpoint.meta}</p>
                </div>
                <DraftTextButton onClick={() => setDraftLine(touchpoint.line)} label={`Draft text for ${name}`} />
              </article>
            );
          })}
        </div>
      )}

      {draftLine && <DraftTextModal line={draftLine} onClose={() => setDraftLine(null)} />}
    </section>
  );
}

function leagueAnchorId(key: string): string {
  return `league-${key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}
// Jump-to bar for the league sections. It sticks to the top of the screen, so
// Scott can move between leagues without scrolling past every table. The active
// chip follows the section at the top of the screen.
function LeagueJumpBar({ sections }: { sections: LeagueSection[] }) {
  const barRef = useRef<HTMLElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  useEffect(() => {
    const offset = barRef.current?.offsetHeight ?? 0;
    document.documentElement.style.setProperty('--jump-offset', `${offset}px`);
    let frame = 0;
    const update = () => {
      frame = 0;
      let current: string | null = null;
      for (const section of sections) {
        const el = document.getElementById(leagueAnchorId(section.key));
        if (el && el.getBoundingClientRect().top <= offset + 16) current = section.key;
      }
      setActiveKey(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    // The app scrolls inside body, not the window, so listen in the capture phase.
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
      document.documentElement.style.removeProperty('--jump-offset');
    };
  }, [sections]);

  // Keep the active chip in view on phones. Scroll only the chip row: scrollIntoView
  // would also stop the page's smooth scroll after a jump.
  useEffect(() => {
    const row = rowRef.current;
    const chip = row?.querySelector<HTMLElement>(`[data-league="${activeKey}"]`);
    if (!row || !chip) return;
    if (chip.offsetLeft < row.scrollLeft || chip.offsetLeft + chip.offsetWidth > row.scrollLeft + row.clientWidth) {
      row.scrollTo({ left: chip.offsetLeft - 48, behavior: 'smooth' });
    }
  }, [activeKey]);

  function jump(key: string) {
    document.getElementById(leagueAnchorId(key))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <nav
      ref={barRef}
      aria-label="Jump to league"
      className="sticky top-0 z-30 -mx-3 border-y border-border bg-white/95 px-3 py-2 shadow-sm backdrop-blur sm:mx-0 sm:rounded-control sm:border"
    >
      <div ref={rowRef} className="relative flex items-center gap-2 overflow-x-auto [scrollbar-width:none]">
        <span className="shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">Jump to</span>
        {sections.map((section) => {
          const active = section.key === activeKey;
          const empty = section.played.length === 0;
          return (
            <button
              key={section.key}
              type="button"
              data-league={section.key}
              aria-current={active ? 'true' : undefined}
              onClick={() => jump(section.key)}
              className={[
                'focus-ring inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[13px] font-semibold transition-colors',
                active
                  ? 'border-bartlett bg-bartlett text-white'
                  : empty
                    ? 'border-border bg-white text-muted hover:text-ink'
                    : 'border-border bg-white text-ink hover:bg-bartlettIce',
              ].join(' ')}
            >
              <span>{section.label}</span>
              <span className={['font-mono text-[11px]', active ? 'text-white/75' : 'text-muted'].join(' ')}>
                {section.played.length}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
function LeagueBlock({
  section,
  statSort,
  onSort,
  showInactive,
  onToggleInactive,
}: {
  section: LeagueSection;
  statSort: StatSort;
  onSort: (key: SortKey) => void;
  showInactive: boolean;
  onToggleInactive: (v: boolean) => void;
}) {
  const played = useMemo(() => sortLines(section.played, statSort), [section.played, statSort]);
  const didNotPlay = useMemo(() => sortLines(section.didNotPlay, statSort), [section.didNotPlay, statSort]);

  return (
    <div
      id={leagueAnchorId(section.key)}
      className="surface-soft space-y-3 rounded-control p-3 sm:p-4"
      style={{ scrollMarginTop: 'calc(var(--jump-offset, 120px) + 8px)' }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          <h2 className="text-base font-semibold">{section.label}</h2>
          {section.sourceStatus === 'unverified' && <span className="text-xs text-amber-700">Source unverified</span>}
          {section.sourceStatus === 'error' && (
            <span className="rounded-full border border-flag-critical/30 bg-flag-critical/5 px-2 py-0.5 font-mono text-[11px] text-flag-critical">
              source error
            </span>
          )}
          {section.sourceStatus === 'stale' && (
            <span className="rounded-full border border-flag-warn/30 bg-flag-warn/10 px-2 py-0.5 font-mono text-[11px] text-flag-warn">
              stale
            </span>
          )}
        </div>
        <span className="rounded-full border border-border bg-white px-2 py-0.5 font-mono text-[12px] text-muted">
          {section.played.length} played
          {section.didNotPlay.length > 0 && ` · ${section.didNotPlay.length} dnp`}
        </span>
      </div>
      {(section.sourceStatus === 'error' || section.sourceStatus === 'stale' || section.sourceStatus === 'unverified') && section.sourceError && (
        <p className={[
          'rounded-control border px-3 py-2 text-[13px]',
          section.sourceStatus === 'error'
            ? 'border-flag-critical/30 bg-flag-critical/5 text-flag-critical'
            : 'border-flag-warn/30 bg-flag-warn/10 text-ink',
        ].join(' ')}>
          {section.sourceError}
        </p>
      )}

      {played.length > 0 && <StatRosterTable lines={played} sort={statSort} onSort={onSort} />}

      {section.played.length === 0 && section.didNotPlay.length === 0 && (
        <p className="rounded-control border border-dashed border-border bg-white/70 px-3 py-3 text-[13px] leading-5 text-muted">
          {emptyLeagueCopy(section.key, section.label)}
        </p>
      )}

      {section.didNotPlay.length > 0 && (
        <div className="space-y-2 pt-1">
          <button
            onClick={() => onToggleInactive(!showInactive)}
            className="focus-ring rounded-full border border-border bg-white px-2 py-1 font-mono text-[11px] text-muted hover:text-ink"
          >
            {showInactive ? '— hide' : '+ show'} {section.didNotPlay.length} did not play
          </button>
          {showInactive &&
            (didNotPlay.length > 0 ? <StatRosterTable lines={didNotPlay} sort={statSort} onSort={onSort} dimmed /> : null)}
        </div>
      )}
    </div>
  );
}

function StatRosterTable({
  lines,
  sort,
  onSort,
  dimmed = false,
}: {
  lines: StatIntelLine[];
  sort: StatSort;
  onSort: (key: SortKey) => void;
  dimmed?: boolean;
}) {
  const [draftLine, setDraftLine] = useState<StatIntelLine | null>(null);

  return (
    <>
      <div className={['space-y-2 sm:hidden', dimmed ? 'opacity-60' : ''].join(' ')}>
        <MobileSortBar sort={sort} onSort={onSort} />
        {lines.map((line) => (
          <MobileStatCard key={line.statLineId} line={line} onDraft={() => setDraftLine(line)} />
        ))}
      </div>
      <div className={['hidden overflow-x-auto rounded-control border border-border bg-white shadow-sm sm:block', dimmed ? 'opacity-60' : ''].join(' ')}>
        <table className="w-full min-w-[1160px] border-separate border-spacing-0 text-left text-[13px]">
          <thead className="border-b border-border bg-bartlettIce/80 font-mono text-[10px] uppercase tracking-[0.08em] text-muted">
            <tr>
              <th className="sticky left-0 z-20 w-[56px] min-w-[56px] bg-bartlettIce px-2 py-2 text-center font-semibold" title="Check once someone has texted this player about the game">
                Texted
              </th>
              <SortableTh className="sticky left-[56px] z-20 w-[220px] bg-bartlettIce shadow-[1px_0_0_rgba(226,222,212,0.95)] sm:w-[260px]" sortKey="player" sort={sort} onSort={onSort}>Player</SortableTh>
              <SortableTh sortKey="position" sort={sort} onSort={onSort}>Pos</SortableTh>
              <SortableTh sortKey="team" sort={sort} onSort={onSort}>Team</SortableTh>
              <SortableTh sortKey="gameDate" sort={sort} onSort={onSort}>Date</SortableTh>
              <SortableTh sortKey="opponent" sort={sort} onSort={onSort}>Opp</SortableTh>
              <SortableTh sortKey="goals" sort={sort} onSort={onSort}>G</SortableTh>
              <SortableTh sortKey="assists" sort={sort} onSort={onSort}>A</SortableTh>
              <SortableTh sortKey="points" sort={sort} onSort={onSort}>P</SortableTh>
              <SortableTh sortKey="plusMinus" sort={sort} onSort={onSort}>+/-</SortableTh>
              <SortableTh sortKey="pim" sort={sort} onSort={onSort}>PIM</SortableTh>
              <SortableTh sortKey="shots" sort={sort} onSort={onSort}>SOG</SortableTh>
              <SortableTh sortKey="hits" sort={sort} onSort={onSort}>HIT</SortableTh>
              <SortableTh sortKey="blocked" sort={sort} onSort={onSort}>BLK</SortableTh>
              <SortableTh sortKey="giveaways" sort={sort} onSort={onSort}>GVA</SortableTh>
              <SortableTh sortKey="takeaways" sort={sort} onSort={onSort}>TKA</SortableTh>
              <SortableTh sortKey="toi" sort={sort} onSort={onSort}>TOI</SortableTh>
              <SortableTh sortKey="goalie" sort={sort} onSort={onSort}>Goalie</SortableTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/80">
            {lines.map((line) => (
              <StatRosterRow key={line.statLineId} line={line} onDraft={() => setDraftLine(line)} />
            ))}
          </tbody>
        </table>
      </div>
      {draftLine && <DraftTextModal line={draftLine} onClose={() => setDraftLine(null)} />}
    </>
  );
}

function MobileSortBar({ sort, onSort }: { sort: StatSort; onSort: (key: SortKey) => void }) {
  const options: Array<{ key: SortKey; label: string }> = [
    { key: 'player', label: 'Player' },
    { key: 'points', label: 'Pts' },
    { key: 'shots', label: 'SOG' },
    { key: 'toi', label: 'TOI' },
    { key: 'plusMinus', label: '+/-' },
  ];

  return (
    <div className="flex gap-1 overflow-x-auto pb-1">
      {options.map((option) => {
        const active = sort.key === option.key;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onSort(option.key)}
            className={[
              'focus-ring inline-flex h-8 shrink-0 items-center gap-1 rounded-full border px-2.5 font-mono text-[11px] font-semibold',
              active ? 'border-bartlett bg-bartlett text-white' : 'border-border bg-white text-muted',
            ].join(' ')}
          >
            <span>{option.label}</span>
            {active && <span className="text-[9px] uppercase opacity-80">{sort.direction}</span>}
          </button>
        );
      })}
    </div>
  );
}

function MobileStatCard({ line, onDraft }: { line: StatIntelLine; onDraft: () => void }) {
  const name = displayName(line);
  const isGoalie = line.position === 'G' || line.decision != null;
  const opponent = line.opponent ? `${line.isHome === 0 ? '@' : 'vs'} ${line.opponent}` : '—';
  const score =
    line.teamScore != null && line.opponentScore != null
      ? `${line.teamScore}-${line.opponentScore}${line.result ? ` ${line.result}` : ''}`
      : line.result ?? null;
  const flags = flagsForLine(line, isGoalie);

  return (
    <article className="rounded-control border border-border bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <TextedCheckbox line={line} />
            <Link to={`/people/${line.playerId}`} className="focus-ring min-w-0 rounded-sm">
              <span className="flex min-w-0 items-baseline gap-1.5">
                {line.jerseyNumber != null && <span className="shrink-0 font-mono text-[11px] text-muted">#{line.jerseyNumber}</span>}
                <span className="truncate text-[15px] font-semibold text-ink">{name}</span>
              </span>
            </Link>
            <DraftTextButton onClick={onDraft} label={`Draft text for ${name}`} />
          </div>
          <span className="mt-0.5 block font-mono text-[11px] text-muted">
            {[line.position, line.team, line.source].filter(Boolean).join(' · ')}
          </span>
          <FormLine line={line} />
          <HighlightLinks line={line} />
        </div>
        <div className="shrink-0 text-right font-mono text-[11px] text-muted">
          <div className="text-ink">{formatGameDay(line.gameDate)}</div>
          <div>{opponent}</div>
          {score && <div className="mt-0.5 text-ink">{score}</div>}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        <BirthdayChip line={line} />
        {flags.map(({ flag, status }) => (
          <CompactFlagChip key={flag} flag={flag} status={status} />
        ))}
      </div>

      {line.isDnp === 1 ? (
        <div className="mt-3 rounded-control border border-dashed border-border bg-bartlettIce/50 px-3 py-2 text-[13px] text-muted">
          {line.dnpReason ?? 'Did not play.'}
        </div>
      ) : (
        <MobileStatGrid line={line} isGoalie={isGoalie} />
      )}
    </article>
  );
}

function MobileStatGrid({ line, isGoalie }: { line: StatIntelLine; isGoalie: boolean }) {
  const stats: Array<[string, string]> = isGoalie
    ? [
        ['DEC', line.decision ?? '—'],
        ['SV', line.saves != null && line.shotsAgainst != null ? `${line.saves}/${line.shotsAgainst}` : '—'],
        ['SV%', formatSavePct(line.savePct)],
        ['GA', num(line.goalsAgainst)],
        ['TOI', formatToi(line.toiSeconds)],
        ['SO', num(line.shutout)],
      ]
    : [
        ['G', num(line.goals)],
        ['A', num(line.assists)],
        ['P', num(line.points)],
        ['+/-', formatPlusMinus(line.plusMinus)],
        ['SOG', num(line.shots)],
        ['TOI', formatToi(line.toiSeconds)],
        ['PIM', num(line.pim)],
        ['HIT', num(line.hits)],
        ['BLK', num(line.blocked)],
        ['GVA', num(line.giveaways)],
        ['TKA', num(line.takeaways)],
      ];

  return (
    <div className="mt-3 grid grid-cols-4 gap-1">
      {stats.map(([label, value]) => (
        <MobileStat key={label} label={label} value={value} />
      ))}
    </div>
  );
}

function MobileStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-control border border-border/70 bg-bartlettIce/50 px-2 py-2">
      <span className="block truncate font-mono text-[10px] font-semibold uppercase text-muted">{label}</span>
      <span className="mt-0.5 block truncate font-mono text-[13px] font-semibold tabular-nums text-ink">{value}</span>
    </div>
  );
}

function StatRosterRow({ line, onDraft }: { line: StatIntelLine; onDraft: () => void }) {
  const name = displayName(line);
  const isGoalie = line.position === 'G' || line.decision != null;
  const opponent = line.opponent ? `${line.isHome === 0 ? '@' : 'vs'} ${line.opponent}` : '—';
  const gameState =
    line.teamScore != null && line.opponentScore != null
      ? `${line.teamScore}-${line.opponentScore}${line.result ? ` ${line.result}` : ''}`
      : line.result ?? '';

  return (
    <tr className="transition-colors hover:bg-bartlettIce/50">
      <td className="sticky left-0 z-10 w-[56px] min-w-[56px] bg-white px-2 py-3 text-center align-middle">
        <TextedCheckbox line={line} />
      </td>
      <PlayerStickyCell line={line} name={name} isGoalie={isGoalie} onDraft={onDraft} />
      <Td muted>{line.position ?? '—'}</Td>
      <Td>{line.team}</Td>
      <Td>
        <span className="font-mono text-[12px] text-ink">{formatGameDay(line.gameDate)}</span>
        <span className="ml-1 font-mono text-[11px] text-muted">{shortDate(line.gameDate)}</span>
      </Td>
      <Td>
        <span className="font-mono text-[12px] text-muted">{opponent}</span>
        {gameState && <span className="ml-2 font-mono text-[12px] text-ink">{gameState}</span>}
      </Td>
      {line.isDnp === 1 ? (
        <td colSpan={12} className="px-3 py-3 text-[13px] text-muted">
          {line.dnpReason ?? 'Did not play.'}
        </td>
      ) : isGoalie ? (
        <>
          <Td muted>—</Td>
          <Td muted>—</Td>
          <Td muted>—</Td>
          <Td muted>—</Td>
          <Td>{num(line.pim)}</Td>
          <Td muted>—</Td>
          <Td muted>—</Td>
          <Td muted>—</Td>
          <Td muted>—</Td>
          <Td muted>—</Td>
          <Td>{formatToi(line.toiSeconds)}</Td>
          <Td>
            {[line.decision, line.saves != null && line.shotsAgainst != null ? `${line.saves}/${line.shotsAgainst}` : null, formatSavePct(line.savePct)]
              .filter((value) => value && value !== '—')
              .join(' · ') || '—'}
          </Td>
        </>
      ) : (
        <>
          <Td stat>{num(line.goals)}</Td>
          <Td stat>{num(line.assists)}</Td>
          <Td stat>{num(line.points)}</Td>
          <Td stat>{formatPlusMinus(line.plusMinus)}</Td>
          <Td stat>{num(line.pim)}</Td>
          <Td stat>{num(line.shots)}</Td>
          <Td stat>{num(line.hits)}</Td>
          <Td stat>{num(line.blocked)}</Td>
          <Td stat>{num(line.giveaways)}</Td>
          <Td stat>{num(line.takeaways)}</Td>
          <Td stat>{formatToi(line.toiSeconds)}</Td>
          <Td muted>—</Td>
        </>
      )}
    </tr>
  );
}

function PlayerStickyCell({
  line,
  name,
  isGoalie,
  onDraft,
}: {
  line: StatIntelLine;
  name: string;
  isGoalie: boolean;
  onDraft: () => void;
}) {
  const flags = flagsForLine(line, isGoalie);

  return (
    <td className="sticky left-[56px] z-10 w-[220px] max-w-[220px] bg-white px-3 py-3 align-middle shadow-[1px_0_0_rgba(226,222,212,0.95)] sm:w-[260px] sm:max-w-[260px]">
      <div className="flex max-w-full items-start gap-1.5">
        <Link to={`/people/${line.playerId}`} className="focus-ring inline-flex min-w-0 flex-col rounded-sm font-semibold text-ink hover:text-bartlett">
          <span className="flex min-w-0 items-baseline gap-1.5">
            {line.jerseyNumber != null && (
              <span className="shrink-0 font-mono text-[11px] font-normal text-muted">#{line.jerseyNumber}</span>
            )}
            <span className="truncate">{name}</span>
          </span>
          <span className="font-mono text-[11px] font-normal text-muted">{line.source}</span>
          <FormLine line={line} />
        </Link>
        <DraftTextButton onClick={onDraft} label={`Draft text for ${name}`} />
      </div>
      <div className="mt-2 flex max-w-full flex-wrap gap-1">
        <BirthdayChip line={line} />
        {flags.map(({ flag, status }) => (
          <CompactFlagChip key={flag} flag={flag} status={status} />
        ))}
      </div>
      <HighlightLinks line={line} />
    </td>
  );
}

// Scott's "Texted" check: one tap marks that someone reached out to this client
// about last night, so Brian and Steve see it without a group text. Green when done.
function TextedCheckbox({ line }: { line: StatIntelLine }) {
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const checked = line.contactedAt != null;
  const name = displayName(line);

  function setContacted(contactedAt: number | null) {
    qc.setQueriesData<StatIntelResponse>({ queryKey: ['stats', 'intel'] }, (data) =>
      data && {
        ...data,
        leagueSections: data.leagueSections.map((section) => ({
          ...section,
          played: section.played.map((item) => (item.playerId === line.playerId && item.gameDate === line.gameDate ? { ...item, contactedAt } : item)),
          didNotPlay: section.didNotPlay.map((item) => (item.playerId === line.playerId && item.gameDate === line.gameDate ? { ...item, contactedAt } : item)),
        })),
      }
    );
  }

  async function toggle() {
    const next = !checked;
    const previous = line.contactedAt ?? null;
    setSaving(true);
    setFailed(false);
    setContacted(next ? Math.floor(Date.now() / 1000) : null);
    try {
      const saved = await apiPost<{ contactedAt: number | null }>(`/api/stats/outreach/${encodeURIComponent(line.playerId)}`, {
        gameDate: line.gameDate,
        contacted: next,
      });
      setContacted(saved.contactedAt);
    } catch {
      setContacted(previous);
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  const when = checked
    ? new Date(line.contactedAt! * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={checked ? `${name} texted. Tap to clear.` : `Mark ${name} as texted`}
      title={failed ? 'Not saved. Tap to try again.' : checked ? `Texted · checked ${when}` : 'Not texted yet'}
      disabled={saving}
      onClick={() => void toggle()}
      className={[
        'focus-ring inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] border transition-colors disabled:opacity-60',
        checked ? 'border-flag-ok bg-flag-ok text-white' : 'border-border bg-white text-transparent hover:border-flag-ok/60',
        failed ? 'border-flag-critical' : '',
      ].join(' ')}
    >
      <Check size={14} strokeWidth={3} aria-hidden="true" />
    </button>
  );
}

const STRENGTH_LABEL: Record<NonNullable<GoalHighlight['strength']>, string> = { ev: '', pp: 'PP', sh: 'SH' };

// Official NHL clips for his goals and assists; the video stays on nhl.com.
function HighlightLinks({ line }: { line: StatIntelLine }) {
  const highlights = line.highlights ?? [];
  if (highlights.length === 0) return null;
  return (
    <span className="mt-1 flex flex-wrap gap-1.5">
      {highlights.map((clip) => {
        const detail = [clip.shotType, clip.strength ? STRENGTH_LABEL[clip.strength] : ''].filter(Boolean).join(', ');
        return (
          <a
            key={`${clip.url}-${clip.role}`}
            href={clip.url}
            target="_blank"
            rel="noopener noreferrer"
            className="focus-ring inline-flex items-center gap-1 rounded-full border border-bartlettGold/45 bg-bartlettGold/15 px-2 py-0.5 text-[11px] font-semibold text-bartlett hover:border-bartlett"
          >
            <PlayCircle size={12} aria-hidden="true" />
            <span>Watch {clip.role}{detail ? ` (${detail})` : ''}</span>
          </a>
        );
      })}
    </span>
  );
}

function FormLine({ line }: { line: StatIntelLine }) {
  const form = line.form;
  if (!form || line.isDnp === 1 || form.last5Games === 0) return null;
  const games = form.last5Games === 1 ? '1 game' : `${form.last5Games} games`;
  return (
    <span className="mt-0.5 block font-mono text-[11px] font-normal text-bartlett">
      Last {games}: {form.last5Points} P{form.pointStreak >= 2 ? ` · ${form.pointStreak}-game point streak` : ''}
    </span>
  );
}

// Moves from the morning roster check. Clear ones are already on the file, with
// Undo; unclear ones (an AHL team that is not the farm club on file) wait for Scott.
function RosterMovesPanel() {
  const qc = useQueryClient();
  const { data } = useRosterMoves();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const items = data?.items ?? [];
  if (items.length === 0) return null;

  async function decide(move: RosterMove, decision: 'undo' | 'apply' | 'dismiss') {
    setBusy(`${move.id}:${decision}`);
    setError(null);
    try {
      await apiPost(`/api/stats/roster-moves/${encodeURIComponent(move.id)}/${decision}`, {});
      await Promise.all([qc.invalidateQueries({ queryKey: ['stats'] }), qc.invalidateQueries({ queryKey: ['players'] })]);
    } catch (err) {
      setError(err instanceof Error ? `Could not save that: ${err.message}` : 'Could not save that.');
    } finally {
      setBusy(null);
    }
  }

  const button = 'focus-ring inline-flex h-9 items-center rounded-control px-3 text-[13px] font-semibold disabled:opacity-50';
  return (
    <section className="rounded-control border border-border bg-white p-3 shadow-sm sm:p-4" aria-labelledby="roster-moves">
      <h2 id="roster-moves" className="flex items-center gap-2 text-base font-semibold tracking-normal">
        <ArrowRightLeft className="h-4 w-4 text-bartlett" /> Roster moves
      </h2>
      <p className="mt-1 text-[13px] text-muted">From the official NHL and AHL rosters, checked each morning.</p>
      <ul className="mt-3 space-y-3 text-[13px]">
        {items.map((move) => (
          <li key={move.id} className="min-w-0 rounded-control border border-border bg-bartlettIce/35 p-3">
            <p>
              <Link to={`/people/${move.playerId}`} className="focus-ring font-semibold text-ink hover:text-bartlett">{move.name}</Link>
              <span className="text-ink">
                {' '}{move.fromTeam ?? 'no team'} ({move.fromLeague ?? '—'}) → {move.toTeam} ({move.toLeague})
              </span>
              <span className={['ml-2 rounded-full border px-2 py-0.5 font-mono text-[11px]', move.status === 'review' ? 'border-flag-warn/40 bg-flag-warn/10 text-ink' : 'border-border bg-white text-muted'].join(' ')}>
                {move.status === 'review' ? 'check this' : move.status === 'applied' ? `updated ${shortDate(move.detectedOn)}` : 'undone'}
              </span>
            </p>
            <p className="mt-1 text-muted">{move.evidence}</p>
            {move.status !== 'undone' && (
              <div className="mt-2 flex flex-wrap gap-2">
                {move.status === 'applied' ? (
                  <button type="button" disabled={busy !== null} onClick={() => decide(move, 'undo')} className={`${button} border border-border bg-white text-ink`}>
                    {busy === `${move.id}:undo` ? 'Saving' : 'Undo'}
                  </button>
                ) : (
                  <>
                    <button type="button" disabled={busy !== null} onClick={() => decide(move, 'apply')} className={`${button} bg-bartlett text-white`}>
                      {busy === `${move.id}:apply` ? 'Saving' : `Update file to ${move.toTeam}`}
                    </button>
                    <button type="button" disabled={busy !== null} onClick={() => decide(move, 'dismiss')} className={`${button} border border-border bg-white text-ink`}>
                      {busy === `${move.id}:dismiss` ? 'Saving' : 'Not a move'}
                    </button>
                  </>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="mt-2 text-[13px] text-flag-critical">{error}</p>}
    </section>
  );
}
function NeedsYouToday({ sections, moves, activeIrCount }: { sections: LeagueSection[]; moves: MoveFlag[]; activeIrCount: number }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const dnp = sections.flatMap((section) => section.didNotPlay);
  const bigNights = sections
    .flatMap((section) => section.played)
    .filter((line) => (line.goals ?? 0) >= 1 || (line.points ?? 0) >= 2 || line.shutout === 1)
    .sort((a, b) => (b.points ?? 0) - (a.points ?? 0) || (b.goals ?? 0) - (a.goals ?? 0));

  async function answerMove(move: MoveFlag, action: 'confirm' | 'dismiss') {
    setBusy(`${move.playerId}:${action}`);
    setMoveError(null);
    try {
      await apiPost(`/api/stats/moves/${encodeURIComponent(move.playerId)}/${action}`, { gameDate: move.gameDate, league: move.statLeague });
      await qc.invalidateQueries({ queryKey: ['stats', 'intel'] });
    } catch (error) {
      setMoveError(error instanceof Error ? `Could not save that answer: ${error.message}` : 'Could not save that answer.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-control border border-border bg-white p-3 shadow-sm sm:p-4" aria-labelledby="needs-you-today">
      <h2 id="needs-you-today" className="text-base font-semibold tracking-normal">Needs you today</h2>
      <div className="mt-3 grid gap-3 lg:grid-cols-3">
        <div className="min-w-0 rounded-control border border-border bg-bartlettIce/35 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold"><UserX className="h-4 w-4 text-flag-critical" /> Scratches and injuries</p>
          {dnp.length === 0 ? (
            <p className="mt-2 text-[13px] text-muted">Every client whose team played was in the lineup.</p>
          ) : (
            <ul className="mt-2 space-y-1.5 text-[13px]">
              {dnp.map((line) => (
                <li key={line.statLineId} className="min-w-0">
                  <Link to={`/people/${line.playerId}`} className="focus-ring font-semibold text-ink hover:text-bartlett">{displayName(line)}</Link>
                  <span className="text-muted"> · {line.league} · {line.dnpReason ?? 'Did not play.'}</span>
                </li>
              ))}
            </ul>
          )}
          {activeIrCount > 0 && (
            <Link to="/ir" className="focus-ring mt-2 inline-block text-[13px] font-semibold text-bartlett underline">
              {activeIrCount} {activeIrCount === 1 ? 'client' : 'clients'} on IR
            </Link>
          )}
        </div>

        <div className="min-w-0 rounded-control border border-border bg-bartlettIce/35 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold"><Flame className="h-4 w-4 text-bartlettGold" /> Big nights</p>
          {bigNights.length === 0 ? (
            <p className="mt-2 text-[13px] text-muted">No goals, multi-point games or shutouts last night.</p>
          ) : (
            <ul className="mt-2 space-y-1.5 text-[13px]">
              {bigNights.map((line) => (
                <li key={line.statLineId} className="min-w-0">
                  <Link to={`/people/${line.playerId}`} className="focus-ring font-semibold text-ink hover:text-bartlett">{displayName(line)}</Link>
                  <span className="font-mono text-muted"> · {line.league} · {line.shutout === 1 ? 'Shutout' : `${line.goals ?? 0}G ${line.assists ?? 0}A`}</span>
                  <HighlightLinks line={line} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="min-w-0 rounded-control border border-border bg-bartlettIce/35 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold"><ArrowRightLeft className="h-4 w-4 text-bartlett" /> Call-ups and send-downs</p>
          {moves.length === 0 ? (
            <p className="mt-2 text-[13px] text-muted">Every client played in the league on his file.</p>
          ) : (
            <ul className="mt-2 space-y-3 text-[13px]">
              {moves.map((move) => {
                const name = move.displayName ?? `${move.firstName} ${move.lastName}`;
                return (
                  <li key={move.playerId} className="min-w-0">
                    <p>
                      <Link to={`/people/${move.playerId}`} className="focus-ring font-semibold text-ink hover:text-bartlett">{name}</Link>
                      <span className="text-muted">
                        {' '}played for {move.statTeam} ({move.statLeague}). His file says {move.fileLeague ?? 'no league'}
                        {move.direction === 'move' ? '.' : `. Possible ${move.direction}.`}
                      </span>
                    </p>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => answerMove(move, 'confirm')}
                        className="focus-ring inline-flex h-9 items-center rounded-control bg-bartlett px-3 text-[13px] font-semibold text-white disabled:opacity-50"
                      >
                        {busy === `${move.playerId}:confirm` ? 'Saving' : `Update file to ${move.statLeague}`}
                      </button>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => answerMove(move, 'dismiss')}
                        className="focus-ring inline-flex h-9 items-center rounded-control border border-border bg-white px-3 text-[13px] font-semibold text-ink disabled:opacity-50"
                      >
                        {busy === `${move.playerId}:dismiss` ? 'Saving' : 'Not a move'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {moveError && <p className="mt-2 text-[13px] text-flag-critical">{moveError}</p>}
        </div>
      </div>
    </section>
  );
}

function formatLongDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function DraftTextButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="focus-ring inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-control border border-bartlettGold/40 bg-bartlettGold/15 text-bartlett transition-colors hover:border-bartlettGold hover:bg-bartlettGold hover:text-bartlett"
    >
      <MessageSquareText size={14} strokeWidth={2.2} />
    </button>
  );
}

function BirthdayChip({ line }: { line: StatIntelLine }) {
  const birthday = birthdayTouchpoint(line.dob);
  if (!birthday) return null;
  return (
    <span
      title={`${birthday.label}: ${formatDob(line.dob)}`}
      className="inline-flex max-w-full items-center rounded-full border border-bartlettGold/45 bg-bartlettGold/15 px-1.5 py-0.5 font-mono text-[10px] leading-4 text-bartlett"
    >
      {birthday.label}
    </span>
  );
}

function DraftTextModal({ line, onClose }: { line: StatIntelLine; onClose: () => void }) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [variant, setVariant] = useState<DraftVariant>('balanced');
  const [revision, setRevision] = useState(0);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'selected' | 'failed'>('idle');
  const [draft, setDraft] = useState(() => buildDraftText(line, { variant: 'balanced' }));
  const [editedDraft, setEditedDraft] = useState(false);
  const [sentText, setSentText] = useState('');
  const [trainingStatus, setTrainingStatus] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
  const scenario = scenarioForLine(line);
  const examples = useTextVoiceExamples(scenario);
  const name = displayName(line);
  const trainedExample = examples.data?.items[0];

  useEffect(() => {
    if (!trainedExample || editedDraft || revision !== 0) return;
    setDraft(adaptTrainingExample(line, trainedExample.sentText));
  }, [editedDraft, line, revision, trainedExample]);

  function setDraftVariant(nextVariant: DraftVariant) {
    setVariant(nextVariant);
    setRevision(0);
    setDraft(buildDraftText(line, { variant: nextVariant, revision: 0 }));
    setEditedDraft(false);
    setCopyStatus('idle');
  }

  function cycleDraft() {
    const nextRevision = revision + 1;
    setRevision(nextRevision);
    setDraft(buildDraftText(line, { variant, revision: nextRevision }));
    setEditedDraft(false);
    setCopyStatus('idle');
  }

  function useTrainedExample() {
    if (!trainedExample) return;
    setDraft(adaptTrainingExample(line, trainedExample.sentText));
    setEditedDraft(false);
    setCopyStatus('idle');
  }

  async function copyDraft() {
    try {
      await navigator.clipboard.writeText(draft);
      setCopyStatus('copied');
      window.setTimeout(() => setCopyStatus('idle'), 1600);
      return;
    } catch {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.focus();
        textarea.select();
        setCopyStatus('selected');
        return;
      }
      setCopyStatus('failed');
    }
  }

  async function saveTrainingExample() {
    const trimmed = sentText.trim();
    if (!trimmed || trainingStatus === 'saving') return;
    setTrainingStatus('saving');
    try {
      await saveTextVoiceExample({
        playerId: line.playerId,
        statLineId: line.statLineId,
        scenario,
        variant,
        draftText: draft,
        sentText: trimmed,
        league: line.league,
        gameDate: line.gameDate,
      });
      await examples.refetch();
      setSentText('');
      setTrainingStatus('saved');
      window.setTimeout(() => setTrainingStatus('idle'), 1800);
    } catch {
      setTrainingStatus('failed');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="draft-text-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/50 p-0 sm:items-center sm:p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-t-control border border-border bg-white shadow-xl sm:rounded-control">
        <div className="flex items-start justify-between gap-3 border-b border-border p-4">
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-bartlettGold">
              Draft text
            </p>
            <h2 id="draft-text-title" className="mt-1 truncate text-lg font-semibold tracking-normal">
              {name}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close draft text"
            className="focus-ring inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-control border border-border text-muted hover:text-ink"
          >
            <X size={17} />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-3 rounded-control border border-border bg-bartlettIce/35 px-2 py-2">
            <span className="px-1 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">Style</span>
            <div className="grid grid-cols-3 gap-1">
              {([
                ['balanced', 'Balanced'],
                ['warm', 'Warm'],
                ['direct', 'Direct'],
              ] as Array<[DraftVariant, string]>).map(([option, label]) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setDraftVariant(option)}
                  className={[
                    'focus-ring h-8 rounded-control px-2 text-sm font-semibold',
                    variant === option ? 'bg-bartlett text-white' : 'border border-border bg-white text-muted hover:text-ink',
                  ].join(' ')}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              setEditedDraft(true);
            }}
            className="focus-ring min-h-44 w-full resize-y rounded-control border border-border bg-bartlettIce/35 p-3 text-[15px] leading-6 outline-none focus:border-bartlett"
          />
          {trainedExample && (
            <div className="rounded-control border border-bartlettGold/40 bg-bartlettGold/10 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-bartlett">
                    Learned voice match
                  </div>
                  <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-muted">{trainedExample.sentText}</p>
                </div>
                <button
                  type="button"
                  onClick={useTrainedExample}
                  className="focus-ring h-8 rounded-control border border-bartlett bg-white px-3 text-sm font-semibold text-bartlett hover:bg-bartlett hover:text-white"
                >
                  Use voice
                </button>
              </div>
            </div>
          )}
          <div className="space-y-2 rounded-control border border-border bg-white p-3">
            <div>
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                Train from actual sent text
              </div>
              <p className="mt-1 text-[13px] leading-5 text-muted">
                Paste the text Scott or Brian actually sent. Future drafts for similar situations will use these examples as voice memory.
              </p>
            </div>
            <textarea
              value={sentText}
              onChange={(event) => setSentText(event.target.value)}
              placeholder={editedDraft ? draft : 'Paste the real text that was sent'}
              className="focus-ring min-h-24 w-full resize-y rounded-control border border-border bg-bartlettIce/35 p-3 text-sm leading-5 outline-none focus:border-bartlett"
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={saveTrainingExample}
                disabled={!sentText.trim() || trainingStatus === 'saving'}
                className="focus-ring inline-flex h-9 items-center justify-center rounded-control border border-bartlett bg-bartlett px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {trainingStatus === 'saving' ? 'Saving...' : trainingStatus === 'saved' ? 'Saved' : trainingStatus === 'failed' ? 'Save failed' : 'Save voice example'}
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={cycleDraft}
              className="focus-ring inline-flex h-10 items-center justify-center gap-2 rounded-control border border-border bg-white px-4 text-sm font-semibold text-muted hover:text-ink"
            >
              <RotateCw size={15} />
              New version
            </button>
            <button
              type="button"
              onClick={copyDraft}
              className="focus-ring inline-flex h-10 items-center justify-center gap-2 rounded-control bg-bartlett px-4 text-sm font-semibold text-white hover:bg-bartlett/90"
            >
              {copyStatus === 'copied' ? <Check size={16} /> : <Copy size={16} />}
              {copyStatus === 'copied' ? 'Copied' : copyStatus === 'selected' ? 'Text selected' : copyStatus === 'failed' ? 'Copy failed' : 'Copy text'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompactFlagChip({ flag, status }: { flag: FlagType; status: FlagStatus }) {
  const copy = FLAG_COPY[flag];
  return (
    <span
      title={`${copy.label}: ${copy[status]}`}
      className={[
        'inline-flex max-w-full items-center gap-1 rounded-full border px-1.5 py-0.5 font-mono text-[10px] leading-4',
        FLAG_TONE[status],
      ].join(' ')}
    >
      <span className="truncate">{copy.label}</span>
      {status !== 'ok' && <span className="font-semibold uppercase">{status}</span>}
    </span>
  );
}

function flagsForLine(line: StatIntelLine, isGoalie: boolean): Array<{ flag: FlagType; status: FlagStatus }> {
  return WATCH_FLAGS.filter((flag) => {
    if (isGoalie && flag !== 'injury-bench') return false;
    if (!supportsToiFlags(line.league) && TOI_FLAG_TYPES.includes(flag)) return false;
    return true;
  })
    .map((flag) => ({
      flag,
      status: flagStatusFor(line, flag),
    }))
    // A tag only earns space on the row when something is wrong.
    .filter(({ status }) => status !== 'ok');
}

function supportsToiFlags(league: string | null | undefined): boolean {
  return (league ?? '').toUpperCase() === 'NHL';
}

function SortableTh({
  children,
  sortKey,
  sort,
  onSort,
  className = '',
}: {
  children: ReactNode;
  sortKey: SortKey;
  sort: StatSort;
  onSort: (key: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  return (
    <th className={['px-3 py-2 font-semibold', className].join(' ')}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={`Sort by ${String(children)}`}
        className={[
          'focus-ring -mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 uppercase tracking-[0.08em]',
          active ? 'text-bartlett' : 'hover:text-ink',
        ].join(' ')}
      >
        <span>{children}</span>
        {active && <span aria-hidden="true" className="text-[9px]">{sort.direction}</span>}
      </button>
    </th>
  );
}

function Td({
  children,
  muted = false,
  stat = false,
}: {
  children: ReactNode;
  muted?: boolean;
  stat?: boolean;
}) {
  return (
    <td className={['px-3 py-3 align-middle', muted ? 'font-mono text-[12px] text-muted' : '', stat ? 'font-mono tabular-nums text-ink' : ''].join(' ')}>
      {children}
    </td>
  );
}

function sortLines(lines: StatIntelLine[], sort: StatSort): StatIntelLine[] {
  return [...lines].sort((a, b) => {
    const primary = compareSortValues(sortValue(a, sort.key), sortValue(b, sort.key));
    if (primary !== 0) return sort.direction === 'asc' ? primary : -primary;
    const byLastName = a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' });
    if (byLastName !== 0) return byLastName;
    return a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' });
  });
}

function sortValue(line: StatIntelLine, key: SortKey): string | number | null {
  switch (key) {
    case 'jersey':
      return line.jerseyNumber;
    case 'player':
      return `${line.lastName} ${line.firstName}`;
    case 'position':
      return line.position;
    case 'team':
      return line.team;
    case 'gameDate':
      return line.gameDate;
    case 'opponent':
      return line.opponent;
    case 'goals':
      return line.goals;
    case 'assists':
      return line.assists;
    case 'points':
      return line.points;
    case 'plusMinus':
      return line.plusMinus;
    case 'pim':
      return line.pim;
    case 'shots':
      return line.shots;
    case 'hits':
      return line.hits;
    case 'blocked':
      return line.blocked;
    case 'giveaways':
      return line.giveaways;
    case 'takeaways':
      return line.takeaways;
    case 'toi':
      return line.toiSeconds;
    case 'goalie':
      return line.decision ?? line.savePct ?? line.saves;
  }
}

function compareSortValues(a: string | number | null, b: string | number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
}

function sortLabel(sort: StatSort): string {
  const labels: Record<SortKey, string> = {
    jersey: '#',
    player: 'Player',
    position: 'Position',
    team: 'Team',
    gameDate: 'Date',
    opponent: 'Opponent',
    goals: 'Goals',
    assists: 'Assists',
    points: 'Points',
    plusMinus: '+/-',
    pim: 'PIM',
    shots: 'Shots',
    hits: 'Hits',
    blocked: 'Blocks',
    giveaways: 'Giveaways',
    takeaways: 'Takeaways',
    toi: 'TOI',
    goalie: 'Goalie',
  };
  return `${labels[sort.key]} ${sort.direction === 'asc' ? 'ascending' : 'descending'}`;
}

function sortFromSearchParams(searchParams: URLSearchParams): StatSort {
  const key = searchParams.get('sort');
  const direction = searchParams.get('dir');
  return {
    key: SORT_KEYS.includes(key as SortKey) ? (key as SortKey) : 'player',
    direction: direction === 'desc' ? 'desc' : 'asc',
  };
}

function formatGameDay(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  });
}

function shortDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function num(value: number | null | undefined, fallback = '—'): string {
  if (value == null) return fallback;
  return String(value);
}

function emptyLeagueCopy(key: string, label: string): string {
  const counts: Record<string, string> = {
    AHL: 'No AHL client rows for this game date. Official AHL game-log ingestion is active; use Refresh sources after final games post.',
    ECHL: 'No ECHL client rows for this game date. Official ECHL profile and game-log ingestion is active; use People for the full ECHL client list.',
    PWHL: "No PWHL client rows for this game date. Official PWHL roster and game-log ingestion is active for matched clients; use People for the full women's pro file cabinet.",
    USHL: 'No USHL client rows for this game date. Official USHL game-log ingestion is active; use Refresh sources after final games post.',
    NCAA: 'No matched NCAA client rows for this game date. College Hockey News final box scores are checked during source refresh.',
    EUROPE: 'Europe coverage is queued behind official league and Flashscore-style feeds. European clients are already organized in People while nightly stat ingest is built out.',
    OTHER: 'No verified game rows for this group on the selected date. Check People for the full player file and source notes.',
  };
  return counts[key] ?? `No ${label} client rows for this game date.`;
}

function LoadingSections() {
  return (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-3">
          <div className="h-4 w-24 animate-pulse rounded bg-border" />
          <div className="h-24 animate-pulse rounded-control border border-border bg-white" />
          <div className="h-24 animate-pulse rounded-control border border-border bg-white" />
        </div>
      ))}
    </div>
  );
}

function NahlMatchReview() {
  type Review = { season: string; players: Array<{ playerId: string; name: string; currentTeam: string | null; candidates: Array<{ sourceId: string; name: string; birthdate: string | null; teams: Array<{ title: string; active: boolean }>; sourceUrl: string; recommendation: string }> }> };
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return <section className="rounded-control border border-border bg-white p-4">
    <button type="button" disabled={loading} className="focus-ring font-semibold text-ink" onClick={async () => {
      setLoading(true); setError(null);
      try { setReview(await apiGet<Review>('/api/admin/nahl-match-review')); }
      catch (e) { setError(e instanceof Error ? e.message : 'NAHL review failed'); }
      finally { setLoading(false); }
    }}>{loading ? 'Checking NAHL player matches…' : 'Review NAHL player matches'}</button>
    {error && <p role="alert" className="text-sm text-flag-critical">{error}</p>}
    {review && <div className="mt-3 space-y-3 text-sm"><p className="text-muted">{review.season} · Review source identity and team before changing a player record.</p>
      {review.players.map((p) => <div key={p.playerId}><Link className="font-semibold underline" to={`/people/${p.playerId}`}>{p.name}</Link> · {p.currentTeam || 'Team missing'}
        {!p.candidates.length && <p className="text-muted">No current-season NAHL candidate. Check league, team, and spelling.</p>}
        {p.candidates.map((c) => <p key={c.sourceId}><a className="underline" href={c.sourceUrl} target="_blank" rel="noreferrer">{c.name}</a> · {c.birthdate || 'Birthdate unavailable'} · {c.teams.map((t) => `${t.title}${t.active ? '' : ' (inactive)'}`).join(', ')} · {c.recommendation === 'review_identity' ? 'Review identity' : 'Team or league needs review'}</p>)}
      </div>)}
    </div>}
  </section>;
}
